import React, {useMemo} from 'react';
import {AccountFields} from '../../shared/fields/keyplay';
import {EnrichedFields} from '../../shared/fields/enrichment';
import {
  Box,
  Flex,
  PopoverContent,
  Popover,
  PopoverHeader,
  PopoverTrigger,
  PopoverArrow,
  PopoverBody,
} from '@chakra-ui/react';
import {useIntentFields} from './useIntentFields';
import {IntentSignalsSection} from './AccountGrid.details';

const _IntentSignalsWithPopover = ({
  enrichedFields,
  accountFields,
  maxCharacters,
}: {
  enrichedFields?: EnrichedFields;
  accountFields: AccountFields;
  maxCharacters: number;
}) => {
  const {intentFields, intentModel} = useIntentFields({
    enrichedFields,
    accountFields,
  });

  const {adjustedFields, totalFields} = useMemo(() => {
    if (!intentModel) {
      return {adjustedFields: [], totalFields: 0};
    }

    const sortedFields = [...intentFields].sort((a, b) => b.points - a.points);

    let runningTotal = 0;
    const trimmedFields = [];
    for (const field of sortedFields) {
      if (runningTotal + field.label.length > maxCharacters) {
        break;
      }
      runningTotal += field.label.length;
      trimmedFields.push(field);
    }

    return {adjustedFields: trimmedFields, totalFields: sortedFields.length};
  }, [intentFields, intentModel, maxCharacters]);

  const hasMoreFields = totalFields > adjustedFields.length;
  const content = (
    <Flex alignItems="baseline" wrap="wrap" gap={2}>
      {adjustedFields.map((field) => (
        <IntentSignal
          key={field.label}
          label={field.label}
          value={field.value}
        />
      ))}
      {hasMoreFields && (
        <Box color="kgray.300" fontSize="xs">
          + {totalFields - adjustedFields.length} more
        </Box>
      )}
    </Flex>
  );

  if (!hasMoreFields) {
    return content;
  }

  return (
    <Popover isLazy={true} placement="auto" trigger="hover">
      <PopoverTrigger>{content}</PopoverTrigger>
      <PopoverContent p={2}>
        <PopoverArrow />
        <PopoverHeader border={0} fontWeight={500}>
          Intent Signals
        </PopoverHeader>
        <PopoverBody maxH="50vh" overflowY="auto">
          <IntentSignalsSection
            enrichedFields={enrichedFields}
            accountFields={accountFields}
          />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export const IntentSignalsWithPopover = React.memo(_IntentSignalsWithPopover);

export const IntentSignal = ({
  label,
  value,
}: {
  label: string;
  value: number;
}) => {
  return (
    <Box
      key={label}
      borderWidth="1px"
      borderColor="kgray.200"
      borderRadius="40px"
      py={0.5}
      px={2}
      color="kgray.400"
      fontSize="xs"
    >
      {label}: {value}
    </Box>
  );
};
